import type {ReactElement} from 'react';
import {useContext} from 'react';
import type {Component} from 'grapesjs';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {FormMakerFormMode, RightPanelView} from '@FormMakerReact/form-maker.types';
import {useSelectedComponent} from '@FormMakerReact/hooks/use-selected-component';
import {getComponentType} from '@FormMakerReact/lib/form-maker-canvas-library';
import {POSTERMYWALL_FORM_BACKGROUND_TYPE} from '@FormMakerReact/components/postermywall-form-background/postermywall-form-background';
import {RightPanelLoadingSkeleton} from './components/right-panel-loading-skeleton/right-panel-loading-skeleton';
import {RightPanelSectorTitle} from './components/right-panel-sector-title/right-panel-sector-title';
import {TabButton} from './components/tab-button/tab-button';
import {KeyboardShortcutsView} from './views/keyboard-shortcuts-view/keyboard-shortcuts-view';
import {getRightPanelSector, resolveSectorTitle} from './right-panel-registry';
import './sectors/form-background-sector/form-background-sector';
import './sectors/heading-sector/heading-sector';
import './sectors/description-sector/description-sector';
import './sectors/icon-vector-sector/icon-vector-sector';
import './sectors/label-sector/label-sector';
import './sectors/submit-button-sector/submit-button-sector';
import styles from './form-maker-right-panel.module.scss';

export function FormMakerRightPanel(): ReactElement {
  const selected = useSelectedComponent();
  const {state, actions} = useContext(formMakerContext);
  const activeMode = state.formMode;
  if (state.rightPanelView === RightPanelView.KEYBOARD_SHORTCUTS) {
    return (
      <aside className={styles.panel} aria-label={window.i18next.t('pmwjs_form_maker_properties_panel_aria')}>
        <KeyboardShortcutsView onBack={actions.closeRightPanelOverlay} />
      </aside>
    );
  }
  return (
    <aside className={styles.panel} aria-label={window.i18next.t('pmwjs_form_maker_properties_panel_aria')}>
      <header className={styles.header}>
        {renderTabStrip(selected, activeMode, state.isEditorLoading, actions.setFormMode)}
        {renderSectorTitle(selected)}
      </header>
      <div className={styles.body}>{renderBody(state.isEditorLoading, selected)}</div>
    </aside>
  );
}

function renderTabStrip(
  selected: Component | null,
  activeMode: FormMakerFormMode,
  isEditorLoading: boolean,
  setFormMode: (mode: FormMakerFormMode) => void
): ReactElement | null {
  if (selected && !shouldHideSectorTitle(selected)) {
    return null;
  }
  return (
    <div className={styles.tabStrip} role="tablist">
      <TabButton
        tab={FormMakerFormMode.SIGNUP}
        label={window.i18next.t('pmwjs_form_maker_tab_signup')}
        isActive={activeMode === FormMakerFormMode.SIGNUP}
        isDisabled={isEditorLoading}
        onSelect={setFormMode}
      />
      <TabButton
        tab={FormMakerFormMode.CONFIRMATION}
        label={window.i18next.t('pmwjs_form_maker_tab_confirmation')}
        isActive={activeMode === FormMakerFormMode.CONFIRMATION}
        isDisabled={isEditorLoading}
        onSelect={setFormMode}
      />
    </div>
  );
}

function renderBody(isEditorLoading: boolean, selected: Component | null): ReactElement | null {
  if (isEditorLoading) {
    return <RightPanelLoadingSkeleton />;
  }
  return renderSelectedSector(selected);
}

function getComponentName(component: Component): string {
  const value: unknown = component.get('name');
  return typeof value === 'string' ? value : '';
}

function shouldHideSectorTitle(component: Component): boolean {
  return getComponentType(component) === POSTERMYWALL_FORM_BACKGROUND_TYPE;
}

function renderSectorTitle(selected: Component | null): ReactElement | null {
  if (!selected || shouldHideSectorTitle(selected)) {
    return null;
  }
  const sector = getRightPanelSector(getComponentType(selected));
  const title = sector ? resolveSectorTitle(sector, selected) : getComponentName(selected);
  return <RightPanelSectorTitle title={title} />;
}

function renderSelectedSector(component: Component | null): ReactElement | null {
  if (!component) {
    return null;
  }
  const type = getComponentType(component);
  const sector = getRightPanelSector(type);
  if (!sector) {
    return <p className={styles.placeholder}>{window.i18next.t('pmwjs_form_maker_no_controls_for_type', {type})}</p>;
  }
  return (
    <>
      {sector.controls.map((Control, idx) => (
        <Control key={idx} component={component} />
      ))}
    </>
  );
}
